import { useRef, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCloseButton,
  type AlertDialogProps as ChakraAlertDialogProps,
} from '@chakra-ui/react';

export interface DialogBoxProps extends Omit<
  ChakraAlertDialogProps,
  'leastDestructiveRef' | 'children'
> {
  header: ReactNode;
  body: ReactNode;
  footer: ReactNode;
  contentProps?: React.ComponentProps<typeof AlertDialogContent>;
  showCloseButton?: boolean;
}

/**
 * DialogBox component is a wrapper around Chakra UI's AlertDialog component.
 */
export function DialogBox({
  isOpen,
  onClose,
  header,
  body,
  footer,
  contentProps,
  showCloseButton = false,
  ...props
}: DialogBoxProps) {
  const leastDestructiveRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={leastDestructiveRef}
      onClose={onClose}
      {...props}
    >
      <AlertDialogOverlay>
        <AlertDialogContent {...contentProps}>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {header}
          </AlertDialogHeader>
          {showCloseButton ? <AlertDialogCloseButton /> : null}
          <AlertDialogBody>{body}</AlertDialogBody>
          <AlertDialogFooter>{footer}</AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export default DialogBox;
